/**
 * ProjectSubmissionForm Component
 * Client-facing form for submitting new project estimation requests
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Alert,
  CircularProgress,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { EstimationService } from '../../services/EstimationService';
import { SubmitEstimationRequest } from '../../types/estimation';

interface ProjectSubmissionFormProps {
  onSubmitSuccess?: (estimationId: number) => void;
  onCancel?: () => void;
}

const PROJECT_TYPES = [
  'Web Application',
  'Mobile Application',
  'E-Commerce',
  'CRM System',
  'API/Backend',
  'Desktop Application',
  'Cloud Infrastructure',
  'Data Analytics',
  'Machine Learning',
  'Other',
];

const BUDGET_RANGES = [
  'Under $10,000',
  '$10,000 - $25,000',
  '$25,000 - $50,000',
  '$50,000 - $100,000',
  '$100,000 - $250,000',
  'Over $250,000',
  'Not Sure',
];

const TIMELINE_OPTIONS = [
  '1-3 months',
  '3-6 months',
  '6-12 months',
  'Over 1 year',
  'Flexible',
];

const TECH_STACK_OPTIONS = [
  'React',
  'Vue.js',
  'Angular',
  'Node.js',
  'Python',
  'PHP',
  'Laravel',
  'Java',
  '.NET',
  'AWS',
  'Azure',
  'Google Cloud',
  'Docker',
  'Kubernetes',
  'PostgreSQL',
  'MySQL',
  'MongoDB',
  'Redis',
  'No Preference',
];

export const ProjectSubmissionForm: React.FC<ProjectSubmissionFormProps> = ({
  onSubmitSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState<SubmitEstimationRequest>({
    project_name: '',
    project_description: '',
    project_types: [],
    estimated_budget_range: '',
    estimated_timeline: '',
    technical_requirements: {},
    business_requirements: {},
    tech_stack_preferences: [],
  });

  const [technicalReqText, setTechnicalReqText] = useState('');
  const [businessReqText, setBusinessReqText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMultiSelectChange = (
    e: SelectChangeEvent<string[]>,
    fieldName: keyof SubmitEstimationRequest
  ) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validation
    if (!formData.project_name.trim()) {
      setError('Project name is required');
      return;
    }

    if (!formData.project_description.trim()) {
      setError('Project description is required');
      return;
    }

    if (formData.project_types.length === 0) {
      setError('Please select at least one project type');
      return;
    }

    setLoading(true);

    try {
      // Parse technical and business requirements
      const submitData: SubmitEstimationRequest = {
        ...formData,
        technical_requirements: technicalReqText
          ? { description: technicalReqText }
          : undefined,
        business_requirements: businessReqText
          ? { description: businessReqText }
          : undefined,
      };

      const response = await EstimationService.submitEstimation(submitData);

      setSuccess(true);
      setError(null);

      // Call success callback after short delay to show success message
      setTimeout(() => {
        if (onSubmitSuccess && response.estimation.id) {
          onSubmitSuccess(response.estimation.id);
        }
      }, 1500);
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(
        err.response?.data?.message ||
          'Failed to submit estimation. Please try again.'
      );
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      project_name: '',
      project_description: '',
      project_types: [],
      estimated_budget_range: '',
      estimated_timeline: '',
      technical_requirements: {},
      business_requirements: {},
      tech_stack_preferences: [],
    });
    setTechnicalReqText('');
    setBusinessReqText('');
    setError(null);
    setSuccess(false);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Submit Project Estimation Request
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Fill out the form below to request a project estimation. Our AI will
          generate clarification questions to help us understand your needs better.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Estimation submitted successfully! You'll receive clarification
            questions shortly.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={3}>
            {/* Project Name */}
            <TextField
              required
              fullWidth
              label="Project Name"
              name="project_name"
              value={formData.project_name}
              onChange={handleInputChange}
              disabled={loading || success}
              placeholder="e.g., Customer Portal Redesign"
            />

            {/* Project Description */}
            <TextField
              required
              fullWidth
              multiline
              rows={4}
              label="Project Description"
              name="project_description"
              value={formData.project_description}
              onChange={handleInputChange}
              disabled={loading || success}
              placeholder="Provide a detailed description of your project goals, target audience, and key features..."
            />

            {/* Project Types & Budget Row */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                <FormControl fullWidth required>
                  <InputLabel>Project Types</InputLabel>
                  <Select
                    multiple
                    value={formData.project_types}
                    onChange={(e) => handleMultiSelectChange(e, 'project_types')}
                    input={<OutlinedInput label="Project Types" />}
                    disabled={loading || success}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {PROJECT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                <FormControl fullWidth>
                  <InputLabel>Estimated Budget Range</InputLabel>
                  <Select
                    name="estimated_budget_range"
                    value={formData.estimated_budget_range}
                    onChange={handleSelectChange}
                    disabled={loading || success}
                    label="Estimated Budget Range"
                  >
                    <MenuItem value="">
                      <em>Not specified</em>
                    </MenuItem>
                    {BUDGET_RANGES.map((range) => (
                      <MenuItem key={range} value={range}>
                        {range}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Timeline & Tech Stack Row */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                <FormControl fullWidth>
                  <InputLabel>Estimated Timeline</InputLabel>
                  <Select
                    name="estimated_timeline"
                    value={formData.estimated_timeline}
                    onChange={handleSelectChange}
                    disabled={loading || success}
                    label="Estimated Timeline"
                  >
                    <MenuItem value="">
                      <em>Not specified</em>
                    </MenuItem>
                    {TIMELINE_OPTIONS.map((timeline) => (
                      <MenuItem key={timeline} value={timeline}>
                        {timeline}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                <FormControl fullWidth>
                  <InputLabel>Tech Stack Preferences</InputLabel>
                  <Select
                    multiple
                    value={formData.tech_stack_preferences || []}
                    onChange={(e) =>
                      handleMultiSelectChange(e, 'tech_stack_preferences')
                    }
                    input={<OutlinedInput label="Tech Stack Preferences" />}
                    disabled={loading || success}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {TECH_STACK_OPTIONS.map((tech) => (
                      <MenuItem key={tech} value={tech}>
                        {tech}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Technical Requirements */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Technical Requirements (Optional)"
              value={technicalReqText}
              onChange={(e) => setTechnicalReqText(e.target.value)}
              disabled={loading || success}
              placeholder="Any specific technical requirements, integrations, performance needs, security requirements, etc."
            />

            {/* Business Requirements */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Business Requirements (Optional)"
              value={businessReqText}
              onChange={(e) => setBusinessReqText(e.target.value)}
              disabled={loading || success}
              placeholder="Business goals, KPIs, user roles, compliance requirements, etc."
            />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              {onCancel && (
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  disabled={loading || success}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading || success}
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || success}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SendIcon />
                }
              >
                {loading ? 'Submitting...' : 'Submit Estimation Request'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProjectSubmissionForm;
